import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchCompanies } from '@/features/companies/api'
import {
  addSiteToGroup,
  createReportGroup,
  deleteReportGroup,
  fetchAvailableSites,
  fetchGroupSites,
  fetchReportGroups,
  fetchStandaloneConfigs,
  removeSiteFromGroup,
  updateReportGroup,
  updateStandaloneConfig,
} from '@/features/reportGroups/api'
import type { ReportFrequency } from '@/features/reportGroups/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/report_groups.html — per-company Report Groups (scheduled,
 * multi-site) and Standalone Configs (single-site). All backing endpoints
 * already existed in admin.py before this migration; the "nested by
 * company" view the legacy page builds server-side is assembled here from
 * the two flat list endpoints, since both already embed company_name.
 */
export default function ReportGroupsPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('reports.manage')
  const queryClient = useQueryClient()

  const groupsQuery = useQuery({ queryKey: ['report-groups'], queryFn: fetchReportGroups })
  const configsQuery = useQuery({ queryKey: ['standalone-configs'], queryFn: fetchStandaloneConfigs })
  const companiesQuery = useQuery({ queryKey: ['companies'], queryFn: fetchCompanies })

  const [search, setSearch] = useState('')
  const [managingGroupId, setManagingGroupId] = useState<number | null>(null)
  const [creatingForCompany, setCreatingForCompany] = useState<string | null>(null)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupFrequency, setNewGroupFrequency] = useState<ReportFrequency>('monthly')

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['report-groups'] })
    queryClient.invalidateQueries({ queryKey: ['standalone-configs'] })
  }

  const createMutation = useMutation({
    mutationFn: () =>
      createReportGroup({ company_id: creatingForCompany!, group_name: newGroupName, frequency: newGroupFrequency }),
    onSuccess: () => {
      setCreatingForCompany(null)
      setNewGroupName('')
      invalidate()
    },
  })
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => updateReportGroup(id, { is_active }),
    onSuccess: invalidate,
  })
  const deleteMutation = useMutation({ mutationFn: deleteReportGroup, onSuccess: invalidate })
  const toggleConfigMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => updateStandaloneConfig(id, { is_active }),
    onSuccess: invalidate,
  })

  const byCompany = useMemo(() => {
    const map = new Map<string, { companyName: string; groups: typeof groupsData; configs: typeof configsData }>()
    const groupsData = groupsQuery.data ?? []
    const configsData = configsQuery.data ?? []
    for (const g of groupsData) {
      if (!map.has(g.company_id)) map.set(g.company_id, { companyName: g.company_name, groups: [], configs: [] })
      map.get(g.company_id)!.groups.push(g)
    }
    for (const c of configsData) {
      if (!map.has(c.company_id)) map.set(c.company_id, { companyName: c.company_name, groups: [], configs: [] })
      map.get(c.company_id)!.configs.push(c)
    }
    return map
  }, [groupsQuery.data, configsQuery.data])

  // Shell (title/search) renders immediately; only the per-company list
  // area below waits on groupsQuery/configsQuery, so the page never goes
  // fully blank while those two are in flight or refetching.
  const isLoading = groupsQuery.isLoading || configsQuery.isLoading
  const isError = groupsQuery.isError || configsQuery.isError

  const entries = Array.from(byCompany.entries()).filter(([, v]) => {
    if (!search) return true
    return v.companyName.toLowerCase().includes(search.toLowerCase())
  })

  return (
    // Same shrink-0-header / flex-1-scroller split as DashboardPage: fills
    // AppLayout's <main> exactly (h-full) so the per-company section list
    // is the ONLY thing that scrolls, with the title/search staying pinned.
    <div className="flex h-full flex-col gap-4">
      <div className="shrink-0 space-y-4">
        <h1 className="text-2xl font-bold text-ink">Reports Configuration</h1>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search company…"
          className="w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
        />

        {isLoading && <LoadingState label="Loading report groups…" />}
        {isError && (
          <ErrorState
            message="Failed to load report groups."
            onRetry={() => {
              groupsQuery.refetch()
              configsQuery.refetch()
            }}
          />
        )}
        {!isLoading && !isError && entries.length === 0 && (
          <p className="text-sm text-text-secondary">No companies with reports configured.</p>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
      {!isLoading &&
        !isError &&
        entries.map(([companyId, { companyName, groups, configs }]) => (
        <section key={companyId} className="rounded-2xl border border-nav-line bg-card p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-ink">{companyName}</h2>
            {canManage && (
              <button
                type="button"
                onClick={() => setCreatingForCompany(creatingForCompany === companyId ? null : companyId)}
                className="text-xs text-accent hover:underline"
              >
                + Add Group
              </button>
            )}
          </div>

          {creatingForCompany === companyId && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (newGroupName.trim()) createMutation.mutate()
              }}
              className="mb-3 flex flex-wrap items-end gap-2 rounded-2xl border border-brand p-3"
            >
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Group Name</label>
                <input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="rounded-xl border border-nav-line bg-input px-3.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Frequency</label>
                <select
                  value={newGroupFrequency}
                  onChange={(e) => setNewGroupFrequency(e.target.value as ReportFrequency)}
                  className="rounded-xl border border-nav-line bg-input px-3.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Create
              </button>
            </form>
          )}

          {groups.length > 0 && (
            <table className="mb-3 w-full text-sm">
              <thead>
                <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                  <th className="py-3 font-medium">Group</th>
                  <th className="py-3 font-medium">Frequency</th>
                  <th className="py-3 font-medium">Sites</th>
                  <th className="py-3 font-medium">Status</th>
                  {canManage && <th className="py-3 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g.id}>
                    <tr className="border-b border-nav-line last:border-0">
                      <td className="py-3 text-ink">{g.group_name}</td>
                      <td className="py-3 text-ink">{g.frequency}</td>
                      <td className="py-3 text-ink">
                        <button
                          type="button"
                          onClick={() => setManagingGroupId(managingGroupId === g.id ? null : g.id)}
                          className="text-accent hover:underline"
                        >
                          {g.site_count} site(s)
                        </button>
                      </td>
                      <td className="py-3 text-ink">{g.is_active ? 'Active' : 'Inactive'}</td>
                      {canManage && (
                        <td className="py-1.5">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => toggleActiveMutation.mutate({ id: g.id, is_active: !g.is_active })}
                              className="text-xs text-text-secondary hover:underline"
                            >
                              {g.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm(`Delete group "${g.group_name}"?`)) deleteMutation.mutate(g.id)
                              }}
                              className="text-xs text-error hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    {managingGroupId === g.id && (
                      <tr className="border-b border-nav-line bg-bg-secondary last:border-0">
                        <td colSpan={5} className="p-3">
                          <ManageSites groupId={g.id} companyId={companyId} canManage={canManage} onChange={invalidate} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}

          {configs.length > 0 && (
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                  <th className="py-3 font-medium">Standalone Site</th>
                  <th className="py-3 font-medium">Frequency</th>
                  <th className="py-3 font-medium">Status</th>
                  {canManage && <th className="py-3 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id} className="border-b border-nav-line last:border-0">
                    <td className="py-3 text-ink">{c.controller_name || c.controller_id}</td>
                    <td className="py-3 text-ink">{c.frequency}</td>
                    <td className="py-3 text-ink">{c.is_active ? 'Active' : 'Inactive'}</td>
                    {canManage && (
                      <td className="py-1.5">
                        <button
                          type="button"
                          onClick={() => toggleConfigMutation.mutate({ id: c.id, is_active: !c.is_active })}
                          className="text-xs text-text-secondary hover:underline"
                        >
                          {c.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
      </div>

      {companiesQuery.isError && null}
    </div>
  )
}

function ManageSites({
  groupId,
  companyId,
  canManage,
  onChange,
}: {
  groupId: number
  companyId: string
  canManage: boolean
  onChange: () => void
}) {
  const queryClient = useQueryClient()
  const sitesQuery = useQuery({ queryKey: ['report-group-sites', groupId], queryFn: () => fetchGroupSites(groupId) })
  const availableQuery = useQuery({
    queryKey: ['available-sites', companyId],
    queryFn: () => fetchAvailableSites(companyId),
    enabled: canManage,
  })
  const [selectedSite, setSelectedSite] = useState('')

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['report-group-sites', groupId] })
    queryClient.invalidateQueries({ queryKey: ['available-sites', companyId] })
    onChange()
  }

  const addMutation = useMutation({
    mutationFn: () => addSiteToGroup(groupId, selectedSite),
    onSuccess: () => {
      setSelectedSite('')
      refresh()
    },
  })
  const removeMutation = useMutation({
    mutationFn: (controllerId: string) => removeSiteFromGroup(groupId, controllerId),
    onSuccess: refresh,
  })

  if (sitesQuery.isLoading) return <LoadingState label="Loading sites…" />

  return (
    <div>
      <ul className="mb-2 space-y-1">
        {(sitesQuery.data ?? []).map((s) => (
          <li key={s.id} className="flex items-center justify-between text-sm text-text-primary">
            <span>{s.controller_name || s.controller_id}</span>
            {canManage && (
              <button
                type="button"
                onClick={() => removeMutation.mutate(s.controller_id)}
                className="text-xs text-error hover:underline"
              >
                Remove
              </button>
            )}
          </li>
        ))}
        {(sitesQuery.data ?? []).length === 0 && <li className="text-sm text-text-secondary">No sites in this group.</li>}
      </ul>
      {canManage && (
        <div className="flex gap-2">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="min-w-0 max-w-full rounded-xl border border-nav-line bg-input px-3.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none"
          >
            <option value="">Add site…</option>
            {(availableQuery.data ?? []).map((s) => (
              <option key={s.controller_id} value={s.controller_id}>
                {s.controller_name || s.controller_id}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => selectedSite && addMutation.mutate()}
            disabled={!selectedSite || addMutation.isPending}
            className="rounded-full bg-brand px-5 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      )}
    </div>
  )
}
